<template>
  <teleport to="body">
    <transition name="fade">
      <div
        v-if="open"
        class="enroll-portal"
        role="dialog"
        aria-modal="true"
        @keydown.esc="close"
      >
        <!-- 蒙板 -->
        <div class="enroll-backdrop" @click="close"></div>

        <!-- 模態框 -->
        <div class="enroll-card" @click.stop>
          <h2 class="enroll-title">課程報名</h2>

          <!-- 成功/錯誤訊息 -->
          <p v-if="msg" :class="['enroll-msg', msgType]">{{ msg }}</p>

          <input
            v-model="name"
            type="text"
            class="enroll-input"
            placeholder="您的名字 Name（可選）"
            :disabled="loading"
            @keydown.enter="submit"
          />

          <input
            v-model="email"
            type="email"
            class="enroll-input"
            placeholder="電子郵箱 Email"
            :disabled="loading"
            @keydown.enter="submit"
          />

          <button
            class="enroll-cta"
            :disabled="!canSubmit || loading"
            @click="submit"
          >
            <span v-if="!loading">進入免費公開課</span>
            <span v-else>送出中…</span>
            <span class="arrow">→</span>
          </button>

          <p class="enroll-note">
            ⚠️ 我們會以Email的形式，發送《Web3項目開發 – 學習藍圖》給您，請注意查收 (垃圾郵件) ！
            請放心，在未經您允許的情況下，您的資料不會洩漏給第三方！
          </p>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { post } from '@/utils/http' // 👈 使用封裝好的 HTTP

const props = defineProps({
  open: { type: Boolean, default: false },
  /** 可從外面指定來源/活動；若未提供，後端會用 default */
  source: { type: String, default: 'landing-web3starter' },
  campaign: { type: String, default: 'solana-free-email-series' }
})
const emit = defineEmits(['close', 'success'])

const name = ref('')
const email = ref('')
const loading = ref(false)
const msg = ref('')
const msgType = ref('') // 'ok' | 'err'

// 僅檢查 email（name 可選）
const canSubmit = computed(() => /^\S+@\S+\.\S+$/.test(email.value.trim()))

function close () {
  if (loading.value) return
  emit('close')
  resetState()
}

function resetState () {
  name.value = ''
  email.value = ''
  msg.value = ''
  msgType.value = ''
  loading.value = false
}

async function submit () {
  if (!canSubmit.value || loading.value) return
  loading.value = true
  msg.value = ''
  msgType.value = ''

  try {
    const payload = {
      email: email.value.trim(),
      name: name.value.trim() || undefined,
      source: props.source || undefined,
      campaign: props.campaign || undefined,
    }

    // 👇 透過封裝的 post，自動帶上 API_BASE 與 Bearer Token（若有）
    const data = await post('/api/leads/optin', payload)

    if (data?.ok === true) {
      const isDup = data?.message === 'ALREADY_OPTED_IN'
      msg.value = isDup
        ? '您已在名單中，我們已為您準備入門郵件 🎉'
        : '報名成功！開啟您的免費公開課 ✨'
      msgType.value = 'ok'

      emit('success', {
        name: payload.name || '',
        email: payload.email,
        leadId: data?.lead_id,
        nextAction: data?.next_action,
      })

      // 清空輸入
      name.value = ''
      email.value = ''
      // 若要自動關閉可改用：emit('close')
    } else {
      // 不預期情況（理論上不會到這）
      msg.value = '未知錯誤，請稍後再試'
      msgType.value = 'err'
    }
  } catch (err) {
    // http.js 會在非 2xx 丟出錯誤，err.message 可能為 INVALID_EMAIL / HTTP_500 等
    if (err?.message === 'INVALID_EMAIL') {
      msg.value = 'Email 格式錯誤，請重新輸入'
    } else if (err?.message) {
      msg.value = err.message
    } else {
      msg.value = '網路異常，請檢查連線後重試'
    }
    msgType.value = 'err'
  } finally {
    loading.value = false
  }
}

// 開/關時鎖定頁面滾動
const lockScroll = (v) => (document.body.style.overflow = v ? 'hidden' : '')
watch(() => props.open, v => lockScroll(v))
onMounted(() => props.open && lockScroll(true))
onBeforeUnmount(() => lockScroll(false))
</script>


<style scoped>
/* ===== 結構層 ===== */
.enroll-portal {
  position: fixed; inset: 0; z-index: 1000;
  display: grid; place-items: center;
  pointer-events: none;
}
.enroll-backdrop {
  position: fixed; inset: 0;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(2px);
  pointer-events: auto;
}

/* ===== 模態框 ===== */
.enroll-card {
  position: relative; z-index: 1001;
  width: min(450px, 90vw);
  background: rgba(255, 255, 255, 0.06);
  backdrop-filter: blur(14px);
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 20px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.35);
  padding: 28px 24px 24px;
  text-align: center;
  pointer-events: auto;
}

/* ===== 標題 ===== */
.enroll-title {
  color: #fff; font-size: 32px; font-weight: 700; margin-bottom: 12px;
}

/* 成功/錯誤訊息 */
.enroll-msg { 
  margin: 6px 0 12px; font-size: 14px; font-weight: 600;
  padding: 8px 12px; border-radius: 10px;
}
.enroll-msg.ok  { color:#fff; background:rgba(16,185,129,.18); border:1px solid rgba(16,185,129,.28) }
.enroll-msg.err { color:#fff; background:rgba(239,68,68,.18);  border:1px solid rgba(239,68,68,.28) }

/* ===== 輸入框 ===== */
.enroll-input {
  width: 100%; margin: 0 auto 14px; padding: 14px 16px;
  border-radius: 14px; border: 1px solid rgba(255, 255, 255, 0.4);
  background: rgba(255, 255, 255, 0.9);
  font-size: 15px; outline: none;
  transition: box-shadow 0.2s, border-color 0.2s, opacity .2s;
}
.enroll-input:focus {
  border-color: #f6c94e;
  box-shadow: 0 0 0 3px rgba(246, 201, 78, 0.25);
}
.enroll-input:disabled { opacity:.7 }

/* ===== CTA ===== */
.enroll-cta {
  width: 100%;
  display:flex; justify-content:center; align-items:center; gap:8px;
  padding: 14px 20px; border-radius: 14px; border: none;
  font-weight: 700; color: #111;
  background: linear-gradient(90deg, #f6c94e, #f1b12a);
  box-shadow: 0 10px 25px rgba(246, 201, 78, 0.3);
  cursor: pointer;
  transition: transform 0.08s, filter 0.15s, opacity .2s;
}
.enroll-cta:hover { filter: brightness(1.05); }
.enroll-cta:active { transform: translateY(1px); }
.enroll-cta:disabled { opacity: 0.6; cursor: not-allowed; }
.enroll-cta .arrow { font-size: 20px; font-weight: 800; }

/* ===== 底部說明 ===== */
.enroll-note {
  margin-top: 14px; font-size: 13px; line-height: 1.6;
  color: rgba(255, 255, 255, 0.85); text-align: left;
}

/* ===== 動畫 ===== */
.fade-enter-active, .fade-leave-active { transition: opacity 0.2s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
</style>
